/**
 * Phase 27.38.2 — SMS Usage panel (KR admin).
 *
 * Weekly per-agent SMS activity. Shows totals at the top plus a
 * ranked list of agents ordered by outbound volume. Reply rate is a
 * quality signal — a high sent count with a low reply rate flags an
 * agent that may need better templates.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  MessageSquare, Loader2, TrendingUp, MessageCircle, Users,
  Send, RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { AgentAvatar } from '@/components/agent/AgentAvatar';
import { smsUsageAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

function StatTile({ icon: Icon, label, value, tone = 'text-slate-900' }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className={`text-2xl font-heading font-semibold ${tone}`}>
        {value}
      </div>
    </div>
  );
}

function ReplyRatePill({ rate, leadsReplied, leadsTexted }) {
  const pct = Math.round((rate || 0) * 100);
  let tone = 'bg-slate-100 text-slate-600 border-slate-200';
  if (pct >= 40) tone = 'bg-emerald-50 text-emerald-700 border-emerald-200';
  else if (pct >= 20) tone = 'bg-sky-50 text-sky-700 border-sky-200';
  else if (pct >= 5) tone = 'bg-amber-50 text-amber-800 border-amber-200';
  else if (leadsTexted > 0) tone = 'bg-rose-50 text-rose-700 border-rose-200';
  return (
    <Badge variant="outline" className={`text-[10px] ${tone}`}>
      <MessageCircle className="w-3 h-3 mr-1" />
      {pct}% · {leadsReplied}/{leadsTexted}
    </Badge>
  );
}

export function SmsUsagePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState('7');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await smsUsageAPI.report(parseInt(windowDays, 10));
      setData(r.data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'No se pudo cargar el reporte de SMS'));
    } finally {
      setLoading(false);
    }
  }, [windowDays]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const agents = data?.agents || [];

  return (
    <Card data-testid="sms-usage-panel">
      <CardHeader className="flex flex-row items-start justify-between pb-3">
        <div>
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <MessageSquare size={16} className="text-sky-600" />
            Uso de SMS por Agente
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            SMS enviados a Leads y tasa de respuesta en los últimos {windowDays} días.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={windowDays} onValueChange={setWindowDays}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="sms-usage-window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 días</SelectItem>
              <SelectItem value="14">Últimos 14 días</SelectItem>
              <SelectItem value="30">Últimos 30 días</SelectItem>
              <SelectItem value="90">Últimos 90 días</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="ghost" size="sm" onClick={load} disabled={loading}
            data-testid="sms-usage-refresh"
          >
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile icon={Send} label="Enviados" value={totals.sent ?? '—'} />
          <StatTile icon={Users} label="Leads contactados" value={totals.leads_texted ?? '—'} />
          <StatTile icon={MessageCircle} label="Leads que respondieron" value={totals.leads_replied ?? '—'} />
          <StatTile
            icon={TrendingUp}
            label="Tasa de respuesta"
            value={totals.reply_rate != null ? `${Math.round((totals.reply_rate || 0) * 100)}%` : '—'}
          />
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-slate-500 gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Cargando…
          </div>
        ) : agents.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg bg-slate-50">
            Ningún agente ha enviado SMS en este período.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm" data-testid="sms-usage-agents-table">
              <thead>
                <tr className="text-left text-[10px] uppercase text-slate-500 tracking-wider border-b border-slate-200">
                  <th className="px-2 py-2">Agente</th>
                  <th className="px-2 py-2 text-right">Enviados</th>
                  <th className="px-2 py-2 text-right hidden sm:table-cell">Recibidos</th>
                  <th className="px-2 py-2 text-right hidden md:table-cell">Leads</th>
                  <th className="px-2 py-2 text-right">Respuesta</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((a) => (
                  <tr
                    key={a.npn}
                    className="border-b border-slate-100 hover:bg-slate-50/60"
                    data-testid={`sms-usage-row-${a.npn}`}
                  >
                    <td className="px-2 py-2">
                      <Link
                        to={`/agents/${a.npn}`}
                        className="flex items-center gap-2 hover:text-sky-700"
                      >
                        <AgentAvatar
                          agent={{
                            npn: a.npn,
                            first_name: a.first_name || '',
                            last_name: a.last_name || '',
                            photo_url: a.photo_url,
                          }}
                          size="w-7 h-7"
                          rounded="full"
                          textSize="text-[10px]"
                          testId={`sms-usage-avatar-${a.npn}`}
                        />
                        <span className="font-medium">
                          {a.first_name} {a.last_name}
                        </span>
                      </Link>
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-sm font-semibold text-slate-800">
                      {a.sent}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-xs text-slate-500 hidden sm:table-cell">
                      {a.received || 0}
                    </td>
                    <td className="px-2 py-2 text-right text-xs text-slate-500 hidden md:table-cell">
                      {a.leads_texted}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <ReplyRatePill
                        rate={a.reply_rate}
                        leadsReplied={a.leads_replied}
                        leadsTexted={a.leads_texted}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default SmsUsagePanel;
